import { useQuery } from "@apollo/client";
import { Card, CardContent, Grid, Typography } from "@material-ui/core";
import { yellow } from "@material-ui/core/colors";
import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorIcon from "@material-ui/icons/Error";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import { useMemo } from "react";
import { AnalyticsListEntry, Location } from "../../graphql/baseTypes";
import { LIST_ALL_LOCATIONS_WITH_COMPANY } from "../../graphql/queries";
import { ListAllLocationsWithCompanyQuery } from "../../graphql/queries/listAllLocationsWithCompany";
import { Company } from "../../graphql/types";
import { LoadingIndicator } from "../Loading";

const useStyles = makeStyles({
  table: {
    minWidth: 650,
  },
});

interface Props {
  items: Array<AnalyticsListEntry>;
  aggregationLoading: boolean;
}

const AnalyticsTable: React.FC<Props> = ({ items, aggregationLoading }) => {
  const classes = useStyles();
  const { data, loading } = useQuery<ListAllLocationsWithCompanyQuery>(
    LIST_ALL_LOCATIONS_WITH_COMPANY,
    {
      onError(error) {
        console.log(error);
      },
    }
  );
  const { listLocations } = data || {};

  const { lookUpLocation } = useMemo<{
    lookUpLocation: { [key: string]: Location & { company: Company } };
  }>(() => {
    const { items } = listLocations || {};
    const locations = items || [];
    const lookUpLocation =
      locations.reduce(
        (acc, current) => ({
          ...acc,
          [current.id]: {
            ...current,
          },
        }),
        {}
      ) || {};

    return { lookUpLocation };
  }, [listLocations]);

  if (loading || aggregationLoading) {
    return <LoadingIndicator />;
  }

  return (
    <TableContainer component={Paper}>
      <Table className={classes.table} size="small" aria-label="a dense table">
        <TableHead>
          <TableRow>
            <TableCell>Firma</TableCell>
            <TableCell align="right">Standort</TableCell>
            <TableCell align="right">Positive</TableCell>
            <TableCell align="right">Negative</TableCell>
            <TableCell align="right">Ungültig</TableCell>
            <TableCell align="right">Gesamt</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((row) => {
            const postiv = row.positiv || 0;
            const negativ = row.negativ || 0;
            const invalid = row.invalid || 0;
            const total = invalid + postiv + negativ;
            return (
              <TableRow key={row.locationId}>
                <TableCell component="th" scope="row">
                  {lookUpLocation[row.locationId].company?.name || ""}
                </TableCell>
                <TableCell align="right">
                  {lookUpLocation[row.locationId].name}
                </TableCell>
                <TableCell align="right">{postiv}</TableCell>
                <TableCell align="right">{negativ}</TableCell>
                <TableCell align="right">{invalid}</TableCell>
                <TableCell align="right">{total || 0}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
  return (
    <Grid item container xs={12} direction="row" spacing={4}>
      {items.map((row) => {
        const postiv = row.positiv || 0;
        const negativ = row.negativ || 0;
        const invalid = row.invalid || 0;
        const total = invalid + postiv + negativ;
        return (
          <Grid item container xs={6} component={Card}>
            <CardContent>
              <Typography variant="h6" color="textSecondary">
                {lookUpLocation[row.locationId].company?.name || ""} am Standort{" "}
                {lookUpLocation[row.locationId].name}
              </Typography>
              <Grid container alignItems="center">
                <Grid
                  item
                  style={{ fill: yellow[500] }}
                  container
                  alignItems="center"
                >
                  <Typography variant="h4" color="inherit">
                    Positive: {postiv}
                  </Typography>
                  <ErrorIcon color={"error"} />
                </Grid>
                <Grid
                  item
                  style={{ fill: yellow[500] }}
                  container
                  alignItems="center"
                >
                  <Typography variant="h4" color="inherit">
                    Ungültig: {invalid}
                  </Typography>
                  <HighlightOffIcon color={"inherit"} />
                </Grid>
                <Grid
                  item
                  style={{ fill: yellow[500] }}
                  container
                  alignItems="center"
                >
                  <Typography variant="h4" color="inherit">
                    Negative: {negativ}
                  </Typography>
                  <CheckCircleOutlineIcon color={"inherit"} />
                </Grid>
                <Grid
                  item
                  style={{ fill: yellow[500] }}
                  container
                  alignItems="center"
                >
                  <Typography variant="h4" color="inherit">
                    Gesamt: {total}
                  </Typography>
                  <HighlightOffIcon color={"inherit"} />
                </Grid>
              </Grid>
            </CardContent>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default AnalyticsTable;
